import { useRef, useMemo, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Code } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  mergeFields?: Array<{ label: string; value: string }>;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = 'Enter your email content...',
  mergeFields = []
}: RichTextEditorProps) {
  const quillRef = useRef<ReactQuill>(null);
  const [showHtml, setShowHtml] = useState(false);
  const [htmlValue, setHtmlValue] = useState(value);

  const insertMergeField = (field: string) => {
    if (!quillRef.current) return;

    const editor = quillRef.current.getEditor();
    const selection = editor.getSelection();

    if (selection) {
      editor.insertText(selection.index, field);
      editor.setSelection(selection.index + field.length, 0);
    } else {
      const length = editor.getLength();
      editor.insertText(length - 1, field);
      editor.setSelection(length + field.length - 1, 0);
    }
  };

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          [{ 'align': [] }],
          ['link'],
          [{ 'color': [] }, { 'background': [] }],
          ['clean']
        ],
      },
      clipboard: {
        matchVisual: false,
      },
    }),
    []
  );

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'align',
    'link',
    'color', 'background'
  ];

  const toggleHtmlView = () => {
    if (showHtml) {
      onChange(htmlValue);
    } else {
      setHtmlValue(value);
    }
    setShowHtml(!showHtml);
  };

  const handleHtmlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setHtmlValue(e.target.value);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        {mergeFields.length > 0 && !showHtml && (
          <div className="flex flex-wrap gap-2">
            <span className="text-sm font-medium text-gray-300">Insert:</span>
            {mergeFields.map((field) => (
              <button
                key={field.value}
                type="button"
                onClick={() => insertMergeField(field.value)}
                className="px-2 py-1 text-xs bg-yellow-400/20 text-yellow-400 rounded hover:bg-yellow-400/30 border border-yellow-400/30 transition-colors"
              >
                {field.label}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={toggleHtmlView}
          className="ml-auto flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded border border-gray-600 transition-colors"
          title={showHtml ? "Switch to Rich Text Editor" : "Switch to HTML Editor"}
        >
          <Code size={16} />
          {showHtml ? 'Rich Text' : 'HTML'}
        </button>
      </div>

      {showHtml ? (
        <div className="border border-gray-700 rounded">
          <textarea
            value={htmlValue}
            onChange={handleHtmlChange}
            className="w-full h-[400px] p-4 font-mono text-sm bg-gray-900 text-gray-100 border-0 focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded"
            placeholder="Edit HTML directly..."
          />
        </div>
      ) : (
        <div className="rich-text-editor-wrapper dark-editor">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value}
            onChange={onChange}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
          />
        </div>
      )}

      <style>{`
        /* Dark theme for Quill editor */
        .dark-editor .ql-toolbar {
          background-color: #1f2937;
          border: 1px solid #374151;
          border-top-left-radius: 0.375rem;
          border-top-right-radius: 0.375rem;
        }

        .dark-editor .ql-container {
          background-color: #111827;
          border: 1px solid #374151;
          border-top: none;
          border-bottom-left-radius: 0.375rem;
          border-bottom-right-radius: 0.375rem;
          min-height: 300px;
          font-size: 16px;
        }

        .dark-editor .ql-editor {
          min-height: 300px;
          color: #f3f4f6;
        }

        .dark-editor .ql-editor.ql-blank::before {
          color: #6b7280;
          font-style: normal;
        }

        /* Toolbar button styles */
        .dark-editor .ql-toolbar .ql-stroke {
          stroke: #9ca3af;
        }

        .dark-editor .ql-toolbar .ql-fill {
          fill: #9ca3af;
        }

        .dark-editor .ql-toolbar .ql-picker-label {
          color: #9ca3af;
        }

        .dark-editor .ql-toolbar button:hover .ql-stroke,
        .dark-editor .ql-toolbar button:focus .ql-stroke,
        .dark-editor .ql-toolbar button.ql-active .ql-stroke {
          stroke: #fbbf24;
        }

        .dark-editor .ql-toolbar button:hover .ql-fill,
        .dark-editor .ql-toolbar button:focus .ql-fill,
        .dark-editor .ql-toolbar button.ql-active .ql-fill {
          fill: #fbbf24;
        }

        .dark-editor .ql-toolbar button:hover .ql-picker-label,
        .dark-editor .ql-toolbar button:focus .ql-picker-label,
        .dark-editor .ql-toolbar .ql-picker-label.ql-active {
          color: #fbbf24;
        }

        /* Picker dropdowns */
        .dark-editor .ql-toolbar .ql-picker-options {
          background-color: #1f2937;
          border: 1px solid #374151;
        }

        .dark-editor .ql-toolbar .ql-picker-item {
          color: #9ca3af;
        }

        .dark-editor .ql-toolbar .ql-picker-item:hover {
          color: #fbbf24;
        }

        /* Color picker */
        .dark-editor .ql-color-picker .ql-picker-options {
          background-color: #1f2937;
          border: 1px solid #374151;
        }

        /* Links */
        .dark-editor .ql-editor a {
          color: #fbbf24;
        }

        /* Selection */
        .dark-editor .ql-editor ::selection {
          background-color: #374151;
        }
      `}</style>
    </div>
  );
}
